import axios from 'axios'

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost/market40/backend'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('super_admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('super_admin_token')
      localStorage.removeItem('super_admin_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/api/auth/login', { username, password })
    return response.data
  },

  logout: async () => {
    const response = await api.post('/api/auth/logout')
    return response.data
  },

  verifyToken: async () => {
    const response = await api.get('/api/auth/me')
    return response.data
  },

  getProfile: async () => {
    const response = await api.get('/api/auth/me')
    return response.data
  }
}

// Users API
export const usersAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin_api.php/users', { params })
    return response.data
  },

  getById: async (id) => {
    const response = await api.get(`/admin_api.php/users/${id}`)
    return response.data
  },

  create: async (userData) => {
    const response = await api.post('/admin_api.php/users', userData)
    return response.data
  },

  update: async (id, userData) => {
    const response = await api.put(`/admin_api.php/users/${id}`, userData)
    return response.data
  },

  delete: async (id) => {
    const response = await api.delete(`/admin_api.php/users/${id}`)
    return response.data
  },

  updateStatus: async (id, status) => {
    const response = await api.patch(`/admin_api.php/users/${id}/status`, { status })
    return response.data
  },

  resetPassword: async (id, newPassword) => {
    const response = await api.post(`/admin_api.php/users/${id}/reset-password`, { password: newPassword })
    return response.data
  },

  getPermissions: async (id) => {
    const response = await api.get(`/admin_api.php/users/${id}/permissions`)
    return response.data
  },

  updatePermissions: async (id, permissions) => {
    const response = await api.put(`/admin_api.php/users/${id}/permissions`, { permissions })
    return response.data
  }
}

// System API
export const systemAPI = {
  getHealth: async () => {
    const response = await api.get('/admin_api.php/system/health')
    return response.data
  },

  getInfo: async () => {
    const response = await api.get('/admin_api.php/system/info')
    return response.data
  },

  clearCache: async () => {
    const response = await api.post('/admin_api.php/system/clear-cache')
    return response.data
  },

  optimizeDatabase: async () => {
    const response = await api.post('/admin_api.php/system/optimize-db')
    return response.data
  },

  getDatabaseStats: async () => {
    const response = await api.get('/admin_api.php/system/db-stats')
    return response.data
  },

  getLogs: async (type = 'error', limit = 100) => {
    const response = await api.get(`/admin_api.php/system/logs`, { 
      params: { type, limit } 
    })
    return response.data
  },

  clearLogs: async (type = 'all') => {
    const response = await api.post('/admin_api.php/system/clear-logs', { type })
    return response.data
  }
}

// Cloudinary API
export const cloudinaryAPI = {
  getStats: async () => {
    const response = await api.get('/admin_api.php/cloudinary/stats')
    return response.data
  },

  getQueue: async () => {
    const response = await api.get('/admin_api.php/cloudinary/queue')
    return response.data
  },

  getUsage: async () => {
    const response = await api.get('/admin_api.php/cloudinary/usage')
    return response.data
  },

  processQueue: async (limit = 10) => {
    const response = await api.post('/admin_api.php/cloudinary/process-queue', { limit })
    return response.data
  },

  processArticleImage: async (articleId, imageUrl) => {
    const response = await api.post('/admin_api.php/cloudinary/process-article', { 
      article_id: articleId, 
      image_url: imageUrl 
    })
    return response.data
  },

  clearQueue: async () => {
    const response = await api.post('/admin_api.php/cloudinary/clear-queue')
    return response.data
  },

  reprocessFailed: async () => {
    const response = await api.post('/admin_api.php/cloudinary/reprocess-failed')
    return response.data
  },

  upload: async (file) => {
    const formData = new FormData()
    formData.append('image', file)
    
    const response = await api.post('/admin_api.php/cloudinary/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  deleteImage: async (publicId) => {
    const response = await api.delete(`/admin_api.php/cloudinary/delete/${publicId}`)
    return response.data
  },

  getTransformations: async () => {
    const response = await api.get('/admin_api.php/cloudinary/transformations')
    return response.data
  },

  optimizeImages: async (options = {}) => {
    const response = await api.post('/admin_api.php/cloudinary/optimize', options)
    return response.data
  }
}

// Debug API
export const debugAPI = {
  getSystemStatus: async () => {
    const response = await api.get('/admin_api.php/debug/system-status')
    return response.data
  },

  getErrorLogs: async (limit = 50) => {
    const response = await api.get('/admin_api.php/debug/error-logs', { 
      params: { limit } 
    })
    return response.data
  },

  getApiLogs: async (limit = 50) => {
    const response = await api.get('/admin_api.php/debug/api-logs', { 
      params: { limit } 
    })
    return response.data
  },

  getPerformanceMetrics: async () => {
    const response = await api.get('/admin_api.php/debug/performance')
    return response.data
  },

  testEndpoint: async (endpoint, method = 'GET', data = null) => {
    const response = await api.post('/admin_api.php/debug/test-endpoint', {
      endpoint,
      method,
      data
    })
    return response.data
  },

  exportLogs: async (type = 'all', format = 'json') => {
    const response = await api.get('/admin_api.php/debug/export-logs', {
      params: { type, format },
      responseType: 'blob'
    })
    return response.data
  }
}

export default api